import * as Y from 'yjs'
import pick from 'lodash.pick'
import * as observable from 'lib0/observable'
import { ClassroomManager } from '@helpers'

import {
  getDocUpdatesRetrospectively,
  getUpdatesToApply,
  requestAnimationFrameByFps
} from './helpers'
import { initialState, reducer, STATUS } from './reducer'
import MediaPlaybackManager from './MediaPlaybackManager'
import { Classroom as ClassroomConstants } from '../../../../constants'
import { AsyncResourcesManager } from './AsyncResourcesManager'
import { FileManager } from './FileManager'
import { Mutex } from './Mutex'

class PlaybackStateObserver extends observable.Observable {
  constructor({ media, getFileUpdates }) {
    super()
    this._fetchFileUpdates = getFileUpdates
    this.media = media
    this.state = reducer(initialState, {})

    this.mux = new Mutex()

    this.asyncResourcesManager = new AsyncResourcesManager()
    this.fileManager = new FileManager(this.asyncResourcesManager)

    this.asyncResourcesManager.on(AsyncResourcesManager.WAITING, () => {
      this.mux.dispatch(() => this.pause(true))
    })
    this.asyncResourcesManager.on(AsyncResourcesManager.READY, () => {
      if (this.getState().loading) {
        this.mux.dispatch(() => this.play())
      }
    })

    this.mediaPlaybackManager = new MediaPlaybackManager()
  }

  dispatch(action) {
    this.state = reducer(this.state, action)
    this.emit('state:update', [this.state])
  }

  getState() {
    return this.state
  }

  init(updates) {
    this.dispatch({
      type: 'init',
      payload: { updates }
    })
  }

  play() {
    if (this.getState().status !== STATUS.IDLE) {
      console.warn('Already playing')
      return Promise.resolve()
    }

    return this.mediaPlaybackManager.play().then(() => {
      const now = performance.now()
      this.dispatch({ type: 'play', payload: { now } })
      this.asyncResourcesManager.isReady()
      this.playbackLoop(now)
    })
  }

  pause(loading = false) {
    if (this.getState().status !== STATUS.PLAYING) {
      if (!this.getState().loading && loading) {
        this.dispatch({ type: 'loading' })
      }

      console.warn('Already paused')
      return Promise.resolve()
    }

    this.dispatch({ type: 'pause', payload: { loading } })
    this.mediaPlaybackManager.pause()
    return Promise.resolve()
  }

  stop() {
    this.mediaPlaybackManager.destroy()
    this.asyncResourcesManager.destroy()
    this.fileManager.destroy()
    this.dispatch({ type: 'stop' })
    this.emit('cleanup', [])
  }

  playbackLoop(now) {
    const state = this.getState()
    if (state.status !== STATUS.PLAYING) return false

    const { updates, elapsed } = getUpdatesToApply(state, now)

    if (updates.length > 0) this.emit('doc:update', [updates])

    if (elapsed > state.terminus - state.genesis) {
      this.stop()
      return false
    }

    this.dispatch({ type: 'progress', payload: { elapsed } })

    return requestAnimationFrameByFps((now) => this.playbackLoop(now), 30)
  }

  async seek(timestamp) {
    const unlock = await this.asyncResourcesManager.lock(`seek-${timestamp}`)

    this.dispatch({
      type: 'seek',
      payload: {
        timestamp
      }
    })

    const observers = ClassroomManager.unobserveAll()

    const frameNames = ['sketch', 'code', 'note']
    const rootObserverKeys = ['panels', 'participants', 'frames']

    const rootDocProvider = ClassroomManager.getProviderByType('root')

    const doc = ClassroomManager.initYDoc(ClassroomConstants.ENV.RECORDING)
    rootDocProvider.doc = doc

    doc.transact(() => {
      this.state.updates
        .filter(
          (update) =>
            update.doc === rootDocProvider.id && update.timestamp <= timestamp
        )
        .forEach(({ payload }) => {
          Y.applyUpdate(doc, payload)
        })
    })

    ClassroomManager.observeMany(pick(observers, rootObserverKeys))

    const fileIds = await Promise.all(
      frameNames
        .map((name) => [name, ClassroomManager.getProviderByType(name)])
        .filter(([, provider]) => !!provider)
        .map(async ([type, { id }]) => {
          await this.fetchFileUpdates(id)
          const updatesToApply = getDocUpdatesRetrospectively(this.state, id)
          ClassroomManager.loadFile({ id, type })
          this.emit('doc:update', [updatesToApply])
          return id
        })
    )

    fileIds.forEach((fileId) => this.fileManager.ready(fileId))

    this.mediaPlaybackManager.seek(this.getState(), this.media)

    unlock()
  }

  fileUpdatesFetched(fileId) {
    return this.state.updates.some((u) => u.doc === fileId)
  }

  fetchFileUpdates(fileId) {
    if (this.fileUpdatesFetched(fileId)) {
      return Promise.resolve({ doc: fileId })
    }

    if (this.fileManager.isLoading(fileId)) {
      return Promise.reject(`Already fetching ${fileId}`)
    }

    this.fileManager.waiting(fileId)

    return this._fetchFileUpdates({
      fileId,
      start: this.state.genesis,
      end: this.state.terminus
    }).then(({ doc, updates, base, type }) => {
      if (!['sketch', 'note', 'code'].includes(type)) {
        return null
      }

      const baseUpdate = {
        id: doc,
        doc,
        payload: new Uint8Array(base),
        createdAt: updates.length
          ? new Date(updates[0].createdAt)
          : this.state.genesis
      }

      this.dispatch({
        type: 'insert_updates',
        payload: {
          updates: [baseUpdate, ...updates]
        }
      })

      return {
        doc
      }
    })
  }

  async loadFileUpdates(fileId) {
    let unlock
    try {
      unlock = await this.asyncResourcesManager.lock(
        `loadFileUpdates-${fileId}`
      )

      const res = await this.fetchFileUpdates(fileId)

      if (!res) {
        return throw `No response from file request`
      }

      const updatesToApply = getDocUpdatesRetrospectively(this.state, res.doc)

      this.emit('doc:update', [updatesToApply])
    } catch (err) {
      console.error(err)
    } finally {
      if (unlock) unlock()
      this.fileManager.ready(fileId)
    }
  }

  registerMediaElem(media, elem) {
    const asyncResource = this.mediaPlaybackManager.register(
      media,
      elem,
      this.getState()
    )

    if (!asyncResource) return
    this.asyncResourcesManager.register(asyncResource)
  }

  unregisterMediaElem(media) {
    const asyncResourceId = this.mediaPlaybackManager.unregister(media)
    this.asyncResourcesManager.unregister(asyncResourceId)
  }

  destroy() {
    this.stop()
    this.asyncResourcesManager.destroy()
    this.fileManager.destroy()
    this.mediaPlaybackManager.destroy()
    super.destroy()
  }
}

export default PlaybackStateObserver
